import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useChatInput } from "../context/ChatInputContext";
import Chatpop from "../components/Chatpop";
import { useAuth } from "../auth/AuthContext";
import useVoiceChat from "../hooks/useVoiceChat";
import { cleanupImageProcessing } from "../utils/imageconvOptimized";
import ChattyMemoryManager from "../utils/chattymemoryManager";
import Message from "../components/Message";
import InChatAd, { chatAdsEnabledFor } from "../components/InChatAd";
import BounceIcon from "../components/BounceIcon";
import { circuitEditor } from "../hooks/useCircuitEditor";
import PdfViewerOverlay from "../components/PdfViewerOverlay";
import { devError } from '../utils/devLog';
import CopyButton from '../components/chat/CopyButton';
import TimestampDivider from '../components/chat/TimestampDivider';
import { shouldShowTimestamp } from '../utils/chatFormatters';
import useChatSessions from '../hooks/useChatSessions';
import useChatMedia from '../hooks/useChatMedia';
import useChatSend from '../hooks/useChatSend';
import {
  ALL_AI_MODELS,
  DEFAULT_AI_MODEL,
  IS_POPUP_ENABLED as isPopupEnabled,
  KAIDEX_AI_API,
  MEMORY_CONFIG
} from "../config/chatConfig";

const EMPTY_IMAGES = [];

// Main component
const Chatty = ({
  messages, setMessages, setMessagesForSession,
  isLoading, setIsLoading, setIsLoadingForSession,
  isFocusMode, sidebarOpen, setSidebarOpen,
}) => {
  // eslint-disable-next-line no-unused-vars
  const { setChatInputProps, setSidebarProps, refreshSessions, sessionsRefreshTrigger, setQnaData, bumpSessionToTop, addSessionToTop, patchSession } = useChatInput();
  // Per-session abort controllers — Map<session_id, AbortController>.
  // Sending in B no longer aborts A's in-flight stream; A keeps streaming
  // in the background while the user works in B. Stop button targets the
  // currently-active session only.
  const abortControllersBySessionRef = useRef(new Map());
  // Legacy single-ref kept as compatibility shim for hooks that still
  // expect `abortControllerRef.current` — points at the active session's
  // controller. Updated whenever currentSessionId changes (effect below).
  const abortControllerRef = useRef(null);
  const intervalIdRef = useRef(null);
  const memoryManagerRef = useRef(null);
  const mockTimeoutPolling = useRef(false);
  const screenshotInProgress = useRef(false);
  const bottomRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const { user, setUser } = useAuth();

  // Local UI state
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isRecording, setIsRecording] = useState(false);
  const [isMimicking, setIsMimicking] = useState(false);
  const [selectedModel, setSelectedModel] = useState(DEFAULT_AI_MODEL);
  const [isLimitReached, setIsLimitReached] = useState(false);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // ==================== SESSION MANAGEMENT (hook) ====================
  const {
    sessions, setSessions,
    currentSessionId, setCurrentSessionId,
    sessionsLoaded,
    isLoadingMessages,
    isCreatingSession, setIsCreatingSession,
    urlSessionId,
    navigate,
    handleCreateSession,
    handleSelectSession,
    handleDeleteSession,
    handleRenameSession,
  } = useChatSessions({ user, messages, setMessages, isLoading, setIsLoading, setSidebarOpen, scrollToBottom, memoryManagerRef });

  // Keep the legacy single abortControllerRef in sync with the
  // currently-active session's controller. New per-session sends use
  // abortControllersBySessionRef directly; this ref exists so the stop
  // button + cleanup paths that still reference abortControllerRef.current
  // operate on the right session.
  useEffect(() => {
    abortControllerRef.current = currentSessionId
      ? abortControllersBySessionRef.current.get(currentSessionId) || null
      : null;
  }, [currentSessionId]);

  // ==================== MEDIA (hook) ====================
  const {
    selectedImages, setSelectedImages,
    uploadImages, setUploadImages,
    handleImageUpload,
    handleScreenshotRequest,
  } = useChatMedia({ setIsLoading, setMessages, selectedModel, currentSessionId, screenshotInProgress });

  // Voice chat integration
  /* eslint-disable no-unused-vars */
  const {
    isConnected: isVoiceConnected,
    isConnecting,
    isUserSpeaking,
    isAISpeaking,
    error: voiceError,
    transcript,
    connect: connectVoice,
    disconnect: disconnectVoice
  } = useVoiceChat(user);
  /* eslint-enable no-unused-vars */

  // Check if this is an empty new chat (for centered welcome UI)
  const isEmptyNewChat = !urlSessionId && messages.length === 0 && !isLoading;

  // ==================== LIFECYCLE EFFECTS ====================

  // Track mount state + body class
  useEffect(() => {
    document.body.classList.add('chatty-page');
    return () => { document.body.classList.remove('chatty-page'); };
  }, []);

  // Compensate sidebar collapse — force reflow after transition
  const prevSidebarOpen = useRef(sidebarOpen);
  useEffect(() => {
    const wasOpen = prevSidebarOpen.current;
    prevSidebarOpen.current = sidebarOpen;
    if (!(wasOpen && !sidebarOpen)) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    setTimeout(() => {
      const scrollPos = container.scrollTop;
      container.scrollTop = scrollPos + 1;
      container.scrollTop = scrollPos;
    }, 350);
  }, [sidebarOpen]);

  // Initialize memory manager
  useEffect(() => {
    memoryManagerRef.current = new ChattyMemoryManager({ ...MEMORY_CONFIG, kaidexEndpoint: KAIDEX_AI_API });
    memoryManagerRef.current.isInitialized = false;
  }, []);

  // Set authenticated user identity in memory manager
  useEffect(() => {
    if (user && user.name && memoryManagerRef.current) {
      memoryManagerRef.current.userIdentity.name = user.name;
      memoryManagerRef.current.userIdentity.lastIdentityUpdate = Date.now();
    }
  }, [user]);

  // Cleanup image processing workers
  useEffect(() => { return () => { cleanupImageProcessing(); }; }, []);

  // ==================== SEND MESSAGE (hook) ====================
  const {
    handleSendMessage,
    handleStopMessage,
    isWebSearching,
    currentTool,
    stopRequested,
  } = useChatSend({
    messages, setMessages, setMessagesForSession,
    isLoading, setIsLoading, setIsLoadingForSession,
    user, setUser, selectedModel, setSelectedModel,
    uploadImages, selectedImages, setSelectedImages, setUploadImages,
    currentSessionId, setCurrentSessionId, setSessions, setIsCreatingSession,
    navigate, refreshSessions, setQnaData,
    abortControllerRef, abortControllersBySessionRef,
    intervalIdRef, mockTimeoutPolling, screenshotInProgress,
    memoryManagerRef, handleScreenshotRequest, handleCreateSession,
    setIsPopupOpen, setIsMimicking, scrollToBottom,
    bumpSessionToTop, addSessionToTop, patchSession,
  });

  // Cleanup abort controller + interval on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current && !stopRequested) abortControllerRef.current.abort();
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    };
  }, [stopRequested]);

  // Register circuit error fix callback
  useEffect(() => {
    circuitEditor.onFixRequest = (errorMsg) => {
      handleSendMessage(errorMsg);
    };
    return () => { circuitEditor.onFixRequest = null; };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // handleSendMessage is now in useChatSend hook
  // handleSendMessage moved to useChatSend hook


  // Auto-scroll disabled - user controls scroll position
  // Only scroll on initial session load, not on every message
  // useEffect(() => {
  //   if (!bottomRef.current) return;
  //   bottomRef.current.scrollIntoView({ behavior: "smooth" });
  // }, [messages]);

  // Handle pending prompt from project page navigation
  useEffect(() => {
    const pendingPrompt = sessionStorage.getItem('pendingPrompt');
    // Only send when currentSessionId matches urlSessionId (prevents race condition)
    if (pendingPrompt && urlSessionId && currentSessionId === urlSessionId && sessionsLoaded) {
      // Clear immediately to prevent re-sending
      sessionStorage.removeItem('pendingPrompt');
      // Small delay to ensure session is ready
      setTimeout(() => {
        handleSendMessage(pendingPrompt);
      }, 100);
    }
  }, [currentSessionId, urlSessionId, sessionsLoaded, handleSendMessage]);

  // Handle pending first message from new chat creation
  useEffect(() => {
    const pendingFirstMessage = sessionStorage.getItem('pendingFirstMessage');
    if (pendingFirstMessage && urlSessionId && currentSessionId === urlSessionId && sessionsLoaded) {
      sessionStorage.removeItem('pendingFirstMessage');
      setIsCreatingSession(false); // Session created, release button loading state
      try {
        const { text, images } = JSON.parse(pendingFirstMessage);
        // Just call handleSendMessage - it will add the message and send to AI
        setTimeout(() => {
          if (images && images.length > 0) {
            setSelectedImages(images);
            // Create placeholder blobs for uploadImages (the actual base64 is in selectedImages)
            setUploadImages(images.map(() => new Blob()));
          }
          handleSendMessage(text);
        }, 50);
      } catch (e) {
        devError("Failed to parse pendingFirstMessage:", e);
        setIsCreatingSession(false);
      }
    }
  }, [currentSessionId, urlSessionId, sessionsLoaded, handleSendMessage]);

  // Use ChatInputContext for fixed input props and sidebar (hook called at top of component)
  const chatInputPropsObj = useMemo(() => ({
    selectedModel,
    setSelectedModel,
    handleSendMessage,
    isLoading,
    isMimicking,
    isCreatingSession,
    handleStopMessage,
    selectedImages,
    setSelectedImages,
    setUploadImages,
    handleImageUpload,
    user,
    isPopupEnabled,
    setIsPopupOpen,
    supportsImages: ALL_AI_MODELS[selectedModel]?.supportsImages ?? false,
    isLimitReached,
    scrollToBottom,
    setMessages, // Add setMessages for voice transcript display
    sidebarOpen, // For chat input to shift with sidebar
    isCentered: isEmptyNewChat, // For centered welcome UI
    currentSessionId, // For voice chat to save to correct session
    navigate, // For voice chat to navigate after creating session
    refreshSessions, // For voice chat to refresh sidebar after creating session
  }), [
    selectedModel,
    handleSendMessage,
    isLoading,
    isMimicking,
    isCreatingSession,
    handleStopMessage,
    selectedImages,
    handleImageUpload,
    sidebarOpen,
    user,
    isLimitReached,
    scrollToBottom,
    setMessages, // Include setMessages in dependencies
    isEmptyNewChat, // For centered UI
    currentSessionId, // For voice chat
    navigate, // For voice chat
    refreshSessions, // For voice chat
  ]);
  // Sidebar props for context
  const sidebarPropsObj = useMemo(() => ({
    isOpen: sidebarOpen,
    sessions,
    currentSessionId,
    onSelectSession: handleSelectSession,
    onCreateSession: handleCreateSession,
    onDeleteSession: handleDeleteSession,
    onRenameSession: handleRenameSession,
    onToggle: () => setSidebarOpen(prev => !prev),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [sidebarOpen, sessions, currentSessionId, handleSelectSession, handleCreateSession, handleDeleteSession, handleRenameSession]);

  useEffect(() => {
    // Provide latest chat input props to fixed input; no cleanup to avoid intermittent nulls
    setChatInputProps(chatInputPropsObj);
  }, [setChatInputProps, chatInputPropsObj]);

  useEffect(() => {
    // Provide sidebar props to App.js for fixed rendering
    setSidebarProps(sidebarPropsObj);
  }, [setSidebarProps, sidebarPropsObj]);

  return (
    <>
      {/* Login popup - outside transformed container for proper z-index */}
      <Chatpop isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />

      {/* 3-column flex layout: Sidebar (via App.js) | Chat | PDF Viewer */}
      <div className="flex h-full w-full">
        {/* Main Content - shifts when sidebar opens */}
        <div
          ref={scrollContainerRef}
          className="flex-1 h-full overflow-auto"
          style={{
            transform: sidebarOpen ? 'translateX(140px) translateZ(0)' : 'translateX(0) translateZ(0)',
            transition: 'transform 0.3s ease',
            willChange: 'transform'
          }}
      >
        <div className={`chatty-page-wrapper w-full relative ${isEmptyNewChat ? '' : 'min-h-screen'}`}
          style={{
            backgroundColor: 'transparent',
            paddingTop: 'calc(var(--navbar-height) + 1rem)',
            paddingBottom: 'calc(var(--footer-height) + 1rem)',
            ...(isEmptyNewChat && {
              // Mobile: footer is fixed, so use full 100vh (footer overlays)
              // Desktop: footer is relative, use 100vh and footer flows after
              height: '100vh',
              overflow: 'hidden'
            })
          }}>


        {/* DEBUG TOOLS - Auto-controlled by debug framework
        
        Measurement overlay for debugging chat alignment:
        - Viewport dimensions and sidebar state
        - CSS variable calculations
        - Element positioning coordinates  
        - Box model properties
        - Code block scroll analysis
        
        Auto-enabled in development (NODE_ENV=development)
        Auto-disabled in production
        */}
        <div className="debug-enabled" style={{
          display: 'none', // Controlled by .debug-enabled CSS class
          position: 'fixed',
          bottom: '10px',
          left: '60px', // Moved from 10px to avoid sidebar overlap
          background: 'rgba(0,0,0,0.9)',
          color: 'white',
          padding: '10px',
          fontFamily: 'monospace',
          fontSize: '12px',
          zIndex: 10000,
          maxWidth: '500px',
          border: '1px solid #333'
        }}>
          <button
            onClick={() => {
              const debugData = [
                `Viewport Width: ${typeof window !== 'undefined' ? window.innerWidth : 0}px`,
                `Sidebar Width: 40px (CSS)`,
                `CSS --sidebar-width-collapsed: ${typeof window !== 'undefined' ? getComputedStyle(document.documentElement).getPropertyValue('--sidebar-width-collapsed') : 'N/A'}`,
                `CSS --content-area-left: ${typeof window !== 'undefined' ? getComputedStyle(document.documentElement).getPropertyValue('--content-area-left') : 'N/A'}`,
                `Content Area: ${typeof window !== 'undefined' ? window.innerWidth - 40 : 0}px`,
                `True Center (collapsed): ${typeof window !== 'undefined' ? 40 + (window.innerWidth - 40) / 2 : 0}px`,
                `True Center (expanded): ${typeof window !== 'undefined' ? 250 + (window.innerWidth - 250) / 2 : 0}px`,
                `Sidebar State: ${typeof window !== 'undefined' ? (document.querySelector('.main-content')?.classList.contains('sidebar-open') ? 'EXPANDED' : 'COLLAPSED') : 'N/A'}`,
                `CSS --content-center: ${typeof window !== 'undefined' ? (() => {
                  const testDiv = document.createElement('div');
                  testDiv.style.left = 'var(--content-center)';
                  testDiv.style.position = 'absolute';
                  document.body.appendChild(testDiv);
                  const computedLeft = getComputedStyle(testDiv).left;
                  document.body.removeChild(testDiv);
                  return computedLeft;
                })() : 'N/A'}`,
                `Chat Input Left: ${typeof window !== 'undefined' ? document.getElementById('chat-input-fixed')?.getBoundingClientRect().left || 'N/A' : 'N/A'}px`,
                `Chat Input Right: ${typeof window !== 'undefined' ? document.getElementById('chat-input-fixed')?.getBoundingClientRect().right || 'N/A' : 'N/A'}px`,
                `Messages Left: ${typeof window !== 'undefined' ? document.querySelector('.chat-messages-container')?.getBoundingClientRect().left || 'N/A' : 'N/A'}px`,
                `Messages Right: ${typeof window !== 'undefined' ? document.querySelector('.chat-messages-container')?.getBoundingClientRect().right || 'N/A' : 'N/A'}px`,
                `Chat Input Border: ${typeof window !== 'undefined' ? getComputedStyle(document.getElementById('chat-input-fixed') || document.createElement('div')).border || 'N/A' : 'N/A'}`,
                `Messages Border: ${typeof window !== 'undefined' ? getComputedStyle(document.querySelector('.chat-messages-container') || document.createElement('div')).border || 'N/A' : 'N/A'}`,
                `Chat Input Box-Sizing: ${typeof window !== 'undefined' ? getComputedStyle(document.getElementById('chat-input-fixed') || document.createElement('div')).boxSizing || 'N/A' : 'N/A'}`,
                `Messages Box-Sizing: ${typeof window !== 'undefined' ? getComputedStyle(document.querySelector('.chat-messages-container') || document.createElement('div')).boxSizing || 'N/A' : 'N/A'}`,
                `CODE BLOCK OVERFLOW-X: ${typeof window !== 'undefined' ? (() => {
                  const codeBlocks = document.querySelectorAll('pre.hljs.overflow-x-auto');
                  const lastCodeBlock = codeBlocks[codeBlocks.length - 1];
                  return lastCodeBlock ? getComputedStyle(lastCodeBlock).overflowX : 'NOT FOUND';
                })() : 'N/A'}`,
                `CODE BLOCK WIDTH: ${typeof window !== 'undefined' ? (() => {
                  const codeBlocks = document.querySelectorAll('pre.hljs.overflow-x-auto');
                  const lastCodeBlock = codeBlocks[codeBlocks.length - 1];
                  return lastCodeBlock ? `${lastCodeBlock.scrollWidth}px (scroll) / ${lastCodeBlock.clientWidth}px (client)` : 'NOT FOUND';
                })() : 'N/A'}`,
                `CODE BLOCK SCROLL: ${typeof window !== 'undefined' ? (() => {
                  const codeBlocks = document.querySelectorAll('pre.hljs.overflow-x-auto');
                  const lastCodeBlock = codeBlocks[codeBlocks.length - 1];
                  return lastCodeBlock ? `scrollLeft: ${lastCodeBlock.scrollLeft}, scrollWidth: ${lastCodeBlock.scrollWidth}, clientWidth: ${lastCodeBlock.clientWidth}` : 'NOT FOUND';
                })() : 'N/A'}`
              ].join('\n');
              navigator.clipboard.writeText(debugData);
            }}
            style={{
              position: 'absolute',
              top: '5px',
              right: '5px',
              background: 'transparent',
              border: '1px solid #666',
              color: 'white',
              padding: '2px 6px',
              fontSize: '10px',
              cursor: 'pointer'
            }}
          >
            Copy
          </button>
          <div>Viewport Width: {typeof window !== 'undefined' ? window.innerWidth : 0}px</div>
          <div>Sidebar Width: 40px (CSS)</div>
          <div>CSS --sidebar-width-collapsed: {typeof window !== 'undefined' ? getComputedStyle(document.documentElement).getPropertyValue('--sidebar-width-collapsed') : 'N/A'}</div>
          <div>CSS --content-area-left: {typeof window !== 'undefined' ? getComputedStyle(document.documentElement).getPropertyValue('--content-area-left') : 'N/A'}</div>
          <div>Content Area: {typeof window !== 'undefined' ? window.innerWidth - 40 : 0}px</div>
          <div>True Center (collapsed): {typeof window !== 'undefined' ? 40 + (window.innerWidth - 40) / 2 : 0}px</div>
          <div>True Center (expanded): {typeof window !== 'undefined' ? 250 + (window.innerWidth - 250) / 2 : 0}px</div>
          <div>Sidebar State: {typeof window !== 'undefined' ? (document.querySelector('.main-content')?.classList.contains('sidebar-open') ? 'EXPANDED' : 'COLLAPSED') : 'N/A'}</div>
          <div>CSS --content-center: {typeof window !== 'undefined' ? (() => {
            const testDiv = document.createElement('div');
            testDiv.style.left = 'var(--content-center)';
            testDiv.style.position = 'absolute';
            document.body.appendChild(testDiv);
            const computedLeft = getComputedStyle(testDiv).left;
            document.body.removeChild(testDiv);
            return computedLeft;
          })() : 'N/A'}</div>
          <div>Chat Input Left: {typeof window !== 'undefined' ? document.getElementById('chat-input-fixed')?.getBoundingClientRect().left || 'N/A' : 'N/A'}px</div>
          <div>Chat Input Right: {typeof window !== 'undefined' ? document.getElementById('chat-input-fixed')?.getBoundingClientRect().right || 'N/A' : 'N/A'}px</div>
          <div>Messages Left: {typeof window !== 'undefined' ? document.querySelector('.chat-messages-container')?.getBoundingClientRect().left || 'N/A' : 'N/A'}px</div>
          <div>Messages Right: {typeof window !== 'undefined' ? document.querySelector('.chat-messages-container')?.getBoundingClientRect().right || 'N/A' : 'N/A'}px</div>
          <div>Chat Input Border: {typeof window !== 'undefined' ? getComputedStyle(document.getElementById('chat-input-fixed') || document.createElement('div')).border || 'N/A' : 'N/A'}</div>
          <div>Messages Border: {typeof window !== 'undefined' ? getComputedStyle(document.querySelector('.chat-messages-container') || document.createElement('div')).border || 'N/A' : 'N/A'}</div>
          <div>Chat Input Box-Sizing: {typeof window !== 'undefined' ? getComputedStyle(document.getElementById('chat-input-fixed') || document.createElement('div')).boxSizing || 'N/A' : 'N/A'}</div>
          <div>Messages Box-Sizing: {typeof window !== 'undefined' ? getComputedStyle(document.querySelector('.chat-messages-container') || document.createElement('div')).boxSizing || 'N/A' : 'N/A'}</div>
          <div>TOTAL CODE BLOCKS: {typeof window !== 'undefined' ? document.querySelectorAll('pre.hljs.overflow-x-auto').length : 0}</div>
          <div>TRACKING: LAST (MOST RECENT) CODE BLOCK</div>
          <div>CODE BLOCK OVERFLOW-X: {typeof window !== 'undefined' ? (() => {
            const codeBlocks = document.querySelectorAll('pre.hljs.overflow-x-auto');
            const lastCodeBlock = codeBlocks[codeBlocks.length - 1];
            return lastCodeBlock ? getComputedStyle(lastCodeBlock).overflowX : 'NOT FOUND';
          })() : 'N/A'}</div>
          <div>CODE BLOCK WIDTH: {typeof window !== 'undefined' ? (() => {
            const codeBlocks = document.querySelectorAll('pre.hljs.overflow-x-auto');
            const lastCodeBlock = codeBlocks[codeBlocks.length - 1];
            return lastCodeBlock ? `${lastCodeBlock.scrollWidth}px (scroll) / ${lastCodeBlock.clientWidth}px (client)` : 'NOT FOUND';
          })() : 'N/A'}</div>
          <div>CODE BLOCK SCROLL: {typeof window !== 'undefined' ? (() => {
            const codeBlocks = document.querySelectorAll('pre.hljs.overflow-x-auto');
            const lastCodeBlock = codeBlocks[codeBlocks.length - 1];
            return lastCodeBlock ? `scrollLeft: ${lastCodeBlock.scrollLeft}, scrollWidth: ${lastCodeBlock.scrollWidth}, clientWidth: ${lastCodeBlock.clientWidth}` : 'NOT FOUND';
          })() : 'N/A'}</div>
          <div>COMPUTED WHITE-SPACE: {typeof window !== 'undefined' ? (() => {
            const codeBlocks = document.querySelectorAll('pre.hljs.overflow-x-auto');
            const lastCodeBlock = codeBlocks[codeBlocks.length - 1];
            return lastCodeBlock ? getComputedStyle(lastCodeBlock).whiteSpace : 'NOT FOUND';
          })() : 'N/A'}</div>
          <div>ACTUAL CSS CLASSES: {typeof window !== 'undefined' ? (() => {
            const codeBlocks = document.querySelectorAll('pre.hljs.overflow-x-auto');
            const lastCodeBlock = codeBlocks[codeBlocks.length - 1];
            return lastCodeBlock ? lastCodeBlock.className : 'NOT FOUND';
          })() : 'N/A'}</div>
          <div>PARENT CONTAINER WIDTH: {typeof window !== 'undefined' ? (() => {
            const codeBlocks = document.querySelectorAll('pre.hljs.overflow-x-auto');
            const lastCodeBlock = codeBlocks[codeBlocks.length - 1];
            return lastCodeBlock ? `${lastCodeBlock.parentElement.clientWidth}px` : 'NOT FOUND';
          })() : 'N/A'}</div>
          <div>PARENT OVERFLOW-X: {typeof window !== 'undefined' ? (() => {
            const codeBlocks = document.querySelectorAll('pre.hljs.overflow-x-auto');
            const lastCodeBlock = codeBlocks[codeBlocks.length - 1];
            return lastCodeBlock ? getComputedStyle(lastCodeBlock.parentElement).overflowX : 'NOT FOUND';
          })() : 'N/A'}</div>
          <div>COMPUTED WIDTH: {typeof window !== 'undefined' ? (() => {
            const codeBlocks = document.querySelectorAll('pre.hljs.overflow-x-auto');
            const lastCodeBlock = codeBlocks[codeBlocks.length - 1];
            return lastCodeBlock ? getComputedStyle(lastCodeBlock).width : 'NOT FOUND';
          })() : 'N/A'}</div>
        </div>

        <div
          ref={messagesContainerRef}
          className="chat-messages-container portrait:pt-[4rem] flex flex-col-reverse max-w-[800px]"
          style={{
            marginLeft: 'calc(50% - 400px + 15.5px)',
            marginRight: 'auto',
            minHeight: '200px'
          }}
        >
          <div ref={bottomRef} />

          {/* Show animated pen while waiting for response - or searching text for web search */}
          {(() => {
            const shouldShowLoading = isLoading && messages.length > 0;

            // devLog(`🖊️ Pen render check: isLoading=${isLoading}, messages.length=${messages.length}, shouldShowLoading=${shouldShowLoading}`);

            if (!shouldShowLoading) return null;
            
            return (
              <div className="w-full mb-6 flex justify-start">
                <div className="flex items-start gap-2 max-w-[85%]" style={{ color: 'var(--text-secondary)' }}>
                  <div className="flex items-center gap-2">
                    {currentTool === 'web_search' || isWebSearching ? (
                      <Message 
                        text="Searching web"
                        role="ai"
                        fontSizeClass="text-base"
                        isSearching={true}
                      />
                    ) : currentTool === 'weather' ? (
                      <Message 
                        text="Checking weather"
                        role="ai"
                        fontSizeClass="text-base"
                        isSearching={true}
                      />
                    ) : (
                      <BounceIcon isLoading={true} />
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Skeleton loading for session messages — only when actually
              loading an existing session (urlSessionId present). Fresh new
              chat has no session to load, so skeleton should not render. */}
          {isLoadingMessages && urlSessionId && (
            <div className="w-full" style={{ height: '100vh', overflow: 'hidden' }}>
              {Array.from({ length: Math.ceil(window.innerHeight / 70) }, (_, i) => {
                const isUser = i % 2 === 1;
                const total = Math.ceil(window.innerHeight / 70);
                const opacity = Math.max(0, 1 - i / total);
                const aiWidths = [[95, 80, 60], [90, 70, 45], [85, 75, 55], [80, 65, 50], [92, 78, 58], [88, 72, 48], [82, 68, 52]];
                const userWidths = [35, 25, 40, 30, 38, 28, 33];
                return (
                  <div key={i} className="w-full mb-6" style={{ opacity }}>
                    {isUser ? (
                      <div className="flex justify-end p-6">
                        <div className="rounded-2xl px-4 py-3 skeleton-pulse" style={{ width: `${userWidths[Math.floor(i/2) % 3]}%`, backgroundColor: 'var(--bg-secondary)' }} />
                      </div>
                    ) : (
                      <div className="flex justify-start px-2">
                        <div className="pt-2 pb-4" style={{ width: '70%' }}>
                          <div className="space-y-2.5">
                            {aiWidths[Math.floor(i/2) % 3].map((w, j) => (
                              <div key={j} className="h-3 rounded skeleton-pulse" style={{ width: `${w}%`, backgroundColor: 'var(--bg-secondary)' }} />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {(() => {
            // Reverse messages for display (newest at bottom with flex-col-reverse)
            const reversedMessages = [...messages].reverse();
            // In-feed ad anchors: after every 5th SUBSTANTIAL AI response, excluding
            // the last message (may still be streaming). Guarded (paid tiers / kill
            // switch) by chatAdsEnabledFor. Keyed by chronological index so the <ins>
            // stays mounted and adsbygoogle.push() never re-fires on the same slot.
            const adAfterChrono = new Set();
            if (chatAdsEnabledFor(user)) {
              const AD_EVERY = 5, AD_MIN_LEN = 200;
              let aiSeen = 0;
              for (let j = 0; j < messages.length - 1; j++) {
                const mm = messages[j];
                if (!(mm && (mm.role === "ai" || mm.role === "assistant"))) continue;
                if (!mm.text || mm.text.length < AD_MIN_LEN) continue;
                aiSeen++;
                if (aiSeen % AD_EVERY === 0) adAfterChrono.add(j);
              }
            }
            return reversedMessages.map((msg, index) => {
              // Get previous message in display order (next in reversed array = previous in time)
              const prevMsgInTime = reversedMessages[index + 1];
              // Only show timestamp above USER messages (when user sent it)
              const showTimestamp = msg.role === "user" && shouldShowTimestamp(msg, prevMsgInTime);

              const isAssistant = msg.role === "ai" || msg.role === "assistant";
              const chronoIdx = messages.length - 1 - index;
              return msg && msg.role ? (
                <React.Fragment key={chronoIdx}>
                  {adAfterChrono.has(chronoIdx) && <InChatAd key={`ad-${chronoIdx}`} />}
                  {/* Message content */}
                  <div
                    className={`w-full ${isAssistant ? "mb-10" : "mb-6"} break-words overflow-visible flex ${
                      msg.role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    {isAssistant && (
                      <div className="pt-2 px-2 portrait:px-0 pb-8 max-w-[80%] portrait:max-w-full min-w-0 relative group" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        <div className="flex items-start gap-2 whitespace-pre-line min-w-0" style={{ color: 'var(--text-secondary)' }}>
                          <div className="flex-1 min-w-0 w-full" style={{ wordBreak: 'break-word' }}>
                            <Message
                              text={msg.text}
                              images={msg.images || (msg.image ? [msg.image] : EMPTY_IMAGES)}
                              role={msg.role}
                              fontSizeClass="text-base"
                              isSearching={msg.isSearching}
                              media={msg.media}
                              sources={msg.sources}
                              diagrams={msg.diagrams}
                              spreadsheets={msg.spreadsheets}
                              widgets={msg.widgets}
                              circuits={msg.circuits}
                              toolsUsed={msg.toolsUsed}
                            />
                          </div>
                        </div>
                        <CopyButton text={msg.text} position="bottom-1 left-2" />
                      </div>
                    )}
                    {msg.role === "user" && (
                      <div className="p-6 max-w-[90%] relative group">
                        <div
                          className={`card-3d chatty-message-bubble px-4 py-2 whitespace-pre-line overflow-x-auto ${(msg.images?.length > 0 || msg.image) ? 'has-image' : ''}`}
                          style={{
                            borderRadius: (msg.images?.length > 0 || msg.image) ? '0' : 'var(--radius-2xl)',
                            color: 'var(--color-text-primary)'
                          }}
                        >
                          <Message
                            text={msg.text}
                            images={msg.images || (msg.image ? [msg.image] : EMPTY_IMAGES)}
                            role={msg.role}
                            fontSizeClass="text-base"
                            isSearching={msg.isSearching}
                          />
                        </div>
                        <CopyButton text={msg.text} position="bottom-1 right-2" />
                      </div>
                    )}
                  </div>
                  {/* Timestamp divider - shown above user message (after in flex-col-reverse) */}
                  {showTimestamp && <TimestampDivider timestamp={msg.timestamp} />}
                </React.Fragment>
              ) : null;
            });
          })()}
        </div>

        </div>
        </div>
        {/* PDF Viewer Desktop now rendered in App.js with fixed positioning */}
      </div>

      {/* PDF Viewer handled by PdfViewerPanel in App.js - responsive for desktop + portrait */}
    </>
  );
};

export default Chatty;